"use client";
import { useMemo, useState } from "react";
import Breadcrumbs from "../../design1/components/BreadCrumb";
import { useEffect } from "react";
import CustomSelect from "../../design1/components/ui/form/CustomSelect";
import { options, ViewProps } from "../../design1/ListingPage1";
import { useRouter, useSearchParams } from "next/navigation";
import { DefaultProperty } from "src/app/components/Icon";
import DefaultPageComponent from "src/app/components/ui/DefaultPageComponent";
import { filterItems } from "../../design1/ListingPage1";
import { PropertyType } from "src/types";
import { propertyTypes } from "../../design1/components/HeroSearchComponent";
import CardGrid from "src/app/components/CardGrid";
import Button from "src/app/components/ui/Button";
import { CardVariant2 } from "./CardVariant2";
import { VariantCard } from "../../design1/components/Listing";
import { statesData } from "src/app/components/constants/statesData";
import {
  SearchIcon,
  PropertySearchData,
  getPriceRanges,
  propertyData,
} from "../../design1/components/HeroSearchComponent";
import Select from "./Select3";
import { UnitsResponse } from "src/app/lib/useFetchUnits";
import { ViewToggle } from "../../design3/Pages/PropertyPage";
import { PropertyCardSkeleton } from "./Skeletons/Design2PropertyCard";
import {
  getMatchingKeys,
  removeMany,
  removeUrlParam,
  usePropertyPurpose,
} from "../../design1/components/HeroSearch";
import useUnitsData from "src/app/components/hooks/useUnitInfo";
import NoNetwork from "src/app/components/ui/NoNetwork";
import NoSearchCriteria from "src/app/components/ui/NoSearchCriteria";
import { useUnits } from "src/app/hooks/useUnits";
import { useUnitFilterTabs } from "src/app/hooks/useUnitsTabs";
const isDemo = process.env.NEXT_PUBLIC_DEMO_MODE === "true";

const View: React.FC<{
  properties: PropertyType[];
  placeholder: UnitsResponse;
  type?: ViewProps;
  text?: string;
  location?: string; // ✅ Add location prop
}> = ({ properties, type, text, placeholder, location }) => {
  const {
    displayedCount,
    handleLoadMore,
    view,
    setView,
    hasRelevantParams,
    setSelectedValue,
    dataToMap,
    totalResults,
    loading,
    error,
    handleSelect,
    hasMore,
    isFetching,
    unitss,
    hydrated,
    selectedValue,
  } = useUnits({ 
    isDemo, 
    placeholder, 
    properties, 
    type,
    location // ✅ Pass location to hook
  });

  // ✅ Better loading state calculation
  const showSkeleton = loading && !isDemo && type === "rent";
  const isShowingAll = displayedCount >= totalResults;

  if (error && type && !isDemo) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    console.error("Property fetch error:", errorMessage);
    return <NoNetwork />;
  }

  if (!loading && !isDemo && type !== "rent") {
    return (
      <DefaultPageComponent
        image="/images/not-found-optimize.gif"
        text={`${text || ""}`}
        svg={DefaultProperty}
      />
    );
  }

  return (
    <section className="pt-10 wrapper">
      <div className="grid-container pb-12">
        <div className="first">
          <div className="first-line flex-wrap gap-3 mb-5 justify-between flex">
            <Breadcrumbs variant="arrow" />
            <div className="others flex gap-4">
              <ViewToggle setView={setView} view={view} />
              <div className="select border border-themeColor px-1 rounded-lg flex items-center">
                <span className="whitespace-nowrap">Sort by:</span>
                <CustomSelect
                  options={options}
                  value={selectedValue}
                  onChange={setSelectedValue}
                  handleSelect={handleSelect}
                  placeholder="Default"
                />
              </div>
            </div>
          </div>
          
          <h2 className="second-line h4 mb-6 text-secondary-main">
            Showing {displayedCount} / {totalResults} results
          </h2>
          
          <Wrapper>
            <Tabs />
          </Wrapper>
          
          <div className="mt-10">
            {/* ✅ Show skeleton during initial load or filter changes */}
            {showSkeleton ? (
              <CardGrid
                maxColumns={view === "list" ? 1 : 3}
                minCardWidth={350}
                gap={20}
              >
                {Array.from({ length: 14 }).map((_, index) => (
                  <PropertyCardSkeleton
                    variant={view === "list" ? true : false}
                    key={index}
                  />
                ))}
              </CardGrid>
            ) : hydrated && !loading && !isDemo && dataToMap.length < 1 ? (
              // ✅ No results state
              hasRelevantParams ? (
                <NoSearchCriteria />
              ) : (
                <div className="text-center">
                  <DefaultPageComponent
                    image="/images/not-found-optimize.gif"
                    text={
                      text ||
                      `We're currently updating our listings. Soon, you'll be able to explore a wide range of properties - whether you're looking to rent, buy, or invest. From residential homes to commercial spaces, we've got something for everyone. Check back shortly to discover your next space!`
                    }
                    svg={DefaultProperty}
                  />
                </div>
              )
            ) : (
              <>
                {/* ✅ Property Grid */}
                <CardGrid gap={20} maxColumns={view === "list" ? 1 : 2}>
                  {(hydrated ? dataToMap : unitss).map((item, index) =>
                    view === "list" ? (
                      <CardVariant2 key={item.id || index} {...item} />
                    ) : (
                      <VariantCard 
                        key={item.id || index} 
                        className="!w-full" 
                        {...item} 
                      />
                    )
                  )}
                </CardGrid>

                {/* ✅ Loading spinner during pagination */}
                {isFetching && hydrated && dataToMap.length > 0 && (
                  <div className="flex justify-center mt-8">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-themeColor"></div>
                  </div>
                )}
              </>
            )}

            {/* ✅ Load More button with better conditions */}
            {hasMore && !isShowingAll && hydrated && dataToMap.length > 0 && (
              <Button
                onClick={()=>{
                  handleLoadMore()
                }}
                disabled={isFetching}
                className="w-max primary-btn block mt-14 !px-4 hover:bg-themeColor/80 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity mx-auto"
              >
                {isFetching ? "Loading..." : "Load More Properties"}
              </Button>
            )}
          </div>
        </div>
        <AdvancedSearch />
      </div>
    </section>
  );
};

export default View;

const Wrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="p-4 bg-white shadow-md rounded-md">{children}</div>;
};
export type TabType =
  | "All"
  | "Land"
  | "House"
  | "Apartment"
  | "Block of Flat"
  | "Commercial";
const Tabs: React.FC = () => {
  const {
    headerItems: headerToMap,
    current,
    currentSub,
    uniqueItems,
    handleHeaderClick,
    handleSubItemClick,
  } = useUnitFilterTabs(isDemo, filterItems);

  return (
    <div className="items  text-secondary-main  py-4">
      <div className="first flex-wrap gap-2 text-lg divide-x divide-disabled-main/50 flex">
        {headerToMap.map((tab) => (
          <span
            key={tab}
            onClick={() => handleHeaderClick(tab as TabType)} // Handle header click
            className={`cursor-pointer relative  px-3 ${
              tab.toLowerCase() === current.toLowerCase()
                ? "text-themeColor font-semibold"
                : ""
            }`}
          >
            <div className="tab relative w-max">
              {tab}
              <span
                className={`absolute left-1/2 -translate-x-1/2 h-[3px] -bottom-1 bg-themeColor w-full rounded md block ${
                  tab === current ? "visible" : "invisible"
                }`}
              ></span>
            </div>
          </span>
        ))}
      </div>
      <div
        className={`second  flex  gap-y-2  flex-wrap ${
          current != "All" && "mt-7"
        }`}
      >
        {current === "All"
          ? ""
          : ["all", ...uniqueItems, "others"].map((item, index) => {
              return (
                <span
                  onClick={() => handleSubItemClick(item)} // Handle sub-item click
                  className={`capitalize ${
                    item === "others" && "hidden"
                  } cursor-pointer hover:text-themeColor ${
                    currentSub?.toLowerCase() === item.toLowerCase() &&
                    "text-themeColor"
                  } px-2 `}
                  key={index}
                >
                  {item}
                </span>
              );
            })}
        {}
      </div>
    </div>
  );
};

const AdvancedSearch = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const propertyPurpose = usePropertyPurpose(); // call this here safely
  // Safe helper function
  const GetFromUrl = (name: string): string | null => {
    return searchParams.get(name);
  };

  const [unitData, setUnitData] = useState({
    unit_type: "",
    unit_sub_type: "",
  });

  // Step 2: Set values using useEffect
  useEffect(() => {
    const getFromUrl = (name: string): string => {
      return searchParams.get(name) || "";
    };

    setUnitData({
      unit_type: getFromUrl("unit_type"),
      unit_sub_type: getFromUrl("unit_sub_type"),
    });
  }, [searchParams]);

  const initialFormData = useMemo(
    () => ({
      search: GetFromUrl("search") || "",
      propertyType: (propertyPurpose as "For rent") || "",
      category: GetFromUrl("category") || "",
      unit_type: unitData.unit_type || GetFromUrl("unit_type") || "",
      min_price: GetFromUrl("min_price") || "",
      max_price: GetFromUrl("max_price") || "",
      unit_sub_type:
        unitData.unit_sub_type || GetFromUrl("unit_sub_type") || "",
      priceRange: "",
      state: GetFromUrl("state") || "",
      local_government: GetFromUrl("local_government") || "",
    }),
    [searchParams, propertyPurpose]
  );
  const [formData, setFormData] = useState<PropertySearchData>(initialFormData);
  const clearAllQueryParams = () => {
    const currentUrl = new URL(window.location.href);
    // Set search to empty to remove all query parameters
    window.history.replaceState(null, "", currentUrl.pathname);
  };
  const handleReset = () => {
    setFormData(initialFormData);
    clearAllQueryParams();
  };
  const { minPrices, maxPrices } = getPriceRanges(formData.propertyType);
  const handleChange = (field: keyof PropertySearchData) => (value: string) => {
    if (!value || value === "") {
      removeUrlParam(field);
    }
    setFormData((prevData) => {
      let updatedData = { ...prevData, [field]: value };
      if (field === "propertyType" && prevData.propertyType !== value) {
        updatedData = {
          ...updatedData,
          category: "",
          unit_type: "",
          unit_sub_type: "",
        };
        removeMany(["category", "unit_type", "unit_sub_type"]);
      }
      if (field === "category" && prevData.category !== value) {
        updatedData = { ...updatedData, unit_type: "", unit_sub_type: "" };
        removeMany(["unit_sub_type", "unit_type"]);
      }
      if (field === "unit_type" && prevData.unit_type !== value) {
        updatedData = { ...updatedData, unit_sub_type: "" };
        removeUrlParam("unit_sub_type");
      }
      if (field === "state" && prevData.state !== value) {
        updatedData = {
          ...updatedData,
          local_government: "",
        };
        removeUrlParam("local_government");
      }
      return updatedData;
    });
  };
  const handleSearch = () => {
    const { category, min_price, max_price, propertyType, ...filters } =
      formData;
    const params = new URLSearchParams();


    // Convert price values
    const convertPrice = (price: string) => {
      if (!price || price === "No Min" || price === "No Max") return "";
      return price
        .replace("₦", "")
        .replace(" Million", "000000")
        .replace(" Billion", "000000000")
        .replace(/,/g, "");
    };
    if (min_price) params.append("min_price", convertPrice(min_price));
    if (max_price) params.append("max_price", convertPrice(max_price));
    if (category) params.append("category", category.toLowerCase());
    // Clean propertyType
    const cleanPropertyType = (type: string) => {
      let cleaned = type
        .replace(/\bfor\b/gi, "")
        .trim()
        .replace(/\s+/g, "-")
        .toLowerCase();

      if (cleaned === "short-let") cleaned = "shortlet";
      return cleaned;
    };
    // Append other filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.append(key, value);
      }
    });
    // Build base path
    let basePath = "/properties";
    if (
      category &&
      ["rent", "sale", "shortlet"].includes(category.toLowerCase())
    ) {
      basePath += `/${category.toLowerCase()}`;
    }
    // Add propertyType as path if it exists
    if (propertyType) {
      const cleanedType = cleanPropertyType(propertyType);
      basePath += `/${cleanedType}`;
    }

    const queryString = params.toString();
    const fullUrl = queryString ? `${basePath}?${queryString}` : basePath;

    // Compare current path (without query) to new path
    const currentPath = window.location.pathname;

    if (currentPath !== basePath) {
      // Path changed → use router.push (full navigation)
      router.push(fullUrl, { scroll: false });
    } else {
      // Path same → only query params changed → replaceState (instant URL update)
      window.history.replaceState(null, "", fullUrl);
    }
  };
  const res = useUnitsData(); // Hook to fetch unit data
  const states = res.unitsData?.cleanedAreas;
  const areas = res.unitsData?.unitInfo;
  const statesToMap = isDemo ? statesData : states;
  const categories = Object.keys(propertyData);
  const typesTest = formData.category
    ? Object.keys(propertyData?.[formData.category] ?? {})
    : [];
  const item = getMatchingKeys(
    typesTest,
    areas as unknown as Record<string, string>
  );

  const types = isDemo ? typesTest : item || [];
  const subtypes = isDemo
    ? formData.category && formData.unit_type
      ? propertyData[formData.category][formData.unit_type]
      : []
    : areas && formData.unit_type
    ? areas[formData.unit_type]
    : [];
  const stateNames = Array.isArray(statesToMap)
    ? statesToMap
        .map((state) => {
          const keys = Object.keys(state);
          return keys.length > 0 ? keys[0] : null;
        })
        .filter((key): key is string => key !== null)
    : [];
  // Get local governments for the selected state
  const selectedStateData = Array.isArray(statesToMap)
    ? statesToMap.find((state) =>
        Object.prototype.hasOwnProperty.call(state, formData.state)
      )
    : undefined;
  const localGovernments = selectedStateData
    ? Object.keys(
        selectedStateData[
          formData.state as keyof typeof selectedStateData
        ] as object
      )
    : [];

  const path = usePropertyPurpose();

  return (
    <div className="form top-[80px] sticky !h-max bg-white p-6 rounded-md">
      <h3 className="text-secondary-main text-xl font-semibold mb-4">
        Advanced Search
      </h3>
      <div className="flex-col flex gap-4">
        <div className="flex border border-themeColor p-3 items-center rounded-md gap-3">
          {" "}
          <SearchIcon />
          <input
            id="keyword"
            name="keyword"
            placeholder="Search with ID or title"
            value={formData.search}
            onChange={(e) => handleChange("search")(e.target.value)}
            className="focus:outline-none outline-none xl:w-[70%]  w-[400px] text-disabled-main border-none rounded"
          />
        </div>
        <Header header="Category">
          <Select
            readonly={path ? true : false}
            placeholder="Select Category"
            name="category"
            value={formData.propertyType}
            options={propertyTypes}
            onChange={handleChange("propertyType")}
          />
          <Select
            placeholder="Select Sub Category"
            name="category"
            value={formData.category}
            options={categories}
            onChange={handleChange("category")}
          />
        </Header>
        <Header header="Property Type">
          <Select
            placeholder="Select Property Type"
            name="type"
            value={formData.unit_type}
            options={types}
            onChange={handleChange("unit_type")}
          />
          <Select
            placeholder="Select Property Subtype"
            name="subtype"
            value={formData.unit_sub_type}
            options={subtypes}
            onChange={handleChange("unit_sub_type")}
          />
        </Header>
        <Header header="Location">
          <Select
            placeholder="Select State"
            name="state"
            value={formData.state}
            options={stateNames}
            onChange={(value: string) => {
              handleChange("state")(value);
              handleChange("local_government")("");
            }}
          />
          <Select
            placeholder="Select LGA"
            value={formData.local_government}
            name="lga"
            options={localGovernments}
            onChange={(value: string) => {
              handleChange("local_government")(value);
            }}
          />
        </Header>
        <Header header="Price Range">
          <Select
            placeholder="Select Minimum Price"
            name="minPrice"
            value={formData.min_price}
            options={minPrices}
            onChange={handleChange("min_price")}
          />
          <Select
            placeholder="Select Maximum Price"
            name="maxPrice"
            value={formData.max_price}
            options={maxPrices}
            onChange={handleChange("max_price")}
          />
        </Header>
      </div>
      <div className="buttons flex mt-4 items-start gap-4">
        <Button
          onClick={handleReset}
          className=" flex justify-center  !p-3 gap-2 items-center border !w-max border-themeColor !text-themeColor bg-white"
        >
          {" "}
          <ResetIcon /> Reset
        </Button>
        <Button onClick={handleSearch} className="flex-1 !p-3 ">
          Find Property
        </Button>
      </div>
    </div>
  );
};

export const Header: React.FC<{
  header: string;
  children: React.ReactNode;
  variant?: boolean;
  design3?: boolean;
}> = ({ header, children, variant, design3 }) => {
  return (
    <div className={`${variant && "flex-1"}`}>
      <h5 className="mb-3 h6 font-medium text-secondary-main">{header}</h5>
      <div className={`flex flex-col  gap-2 ${design3 && "xs:!flex-row"}`}>
        {children}
      </div>
    </div>
  );
};

export const ResetIcon = () => {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.33253 13.3333H4.16586V17.5M11.6659 6.66667H15.8325V2.5M3.81836 7.5025C4.28569 6.3462 5.068 5.34418 6.07638 4.61031C7.08477 3.87643 8.27879 3.44013 9.52277 3.35098C10.7667 3.26183 12.0108 3.52342 13.1135 4.10601C14.2162 4.68859 15.1334 5.56882 15.7609 6.64667M16.1809 12.4975C15.7136 13.654 14.9313 14.6562 13.9228 15.3903C12.9144 16.1243 11.7202 16.5607 10.4761 16.6499C9.23195 16.739 7.98775 16.4773 6.88493 15.8946C5.7821 15.3119 4.86488 14.4314 4.23753 13.3533"
        stroke="var(--primary-color)"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
};
